import {
  $getRoot,
  $createTextNode,
  $createParagraphNode,
  $isElementNode,
  type EditorState,
  type LexicalEditor
} from "lexical";
import { $createMentionNode, $isMentionNode } from "@/lib/lexical/nodes/MentionNode";

// The @[Name](userId) token format intentionally stores only name and ID.
// Email is not preserved on roundtrip — re-hydrated mentions will have email: "".
export type MentionSegment = { type: "text"; text: string } | { type: "mention"; userId: string; name: string };

export function splitMentionName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || "" };
}

const MENTION_TOKEN_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;

export function parseMentions(content: string): MentionSegment[] {
  const segments: MentionSegment[] = [];
  let lastIndex = 0;

  for (const match of content.matchAll(MENTION_TOKEN_REGEX)) {
    const matchStart = match.index!;
    if (matchStart > lastIndex) {
      segments.push({ type: "text", text: content.slice(lastIndex, matchStart) });
    }
    segments.push({ type: "mention", name: match[1], userId: match[2] });
    lastIndex = matchStart + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({ type: "text", text: content.slice(lastIndex) });
  }

  return segments;
}

export function serializeWithMentions(editorState: EditorState): string {
  let result = "";
  editorState.read(() => {
    const root = $getRoot();
    const paragraphs = root.getChildren();
    paragraphs.forEach((paragraph, pIndex) => {
      if (pIndex > 0) result += "\n";
      if (!$isElementNode(paragraph)) {
        result += paragraph.getTextContent();
        return;
      }
      const children = paragraph.getChildren();
      for (const child of children) {
        if ($isMentionNode(child)) {
          const user = child.getUser();
          result += `@[${[user.firstName, user.lastName].filter(Boolean).join(" ")}](${user.id})`;
        } else {
          result += child.getTextContent();
        }
      }
    });
  });
  return result;
}

export function initEditorWithMentions(editor: LexicalEditor, content: string) {
  editor.update(
    () => {
      const root = $getRoot();
      root.clear();

      const lines = content.split("\n");
      for (const line of lines) {
        const paragraph = $createParagraphNode();
        const segments = parseMentions(line);

        for (const segment of segments) {
          if (segment.type === "text") {
            if (segment.text) {
              paragraph.append($createTextNode(segment.text));
            }
          } else {
            const { firstName, lastName } = splitMentionName(segment.name);
            paragraph.append($createMentionNode({ id: segment.userId, firstName, lastName, email: "" }));
          }
        }

        root.append(paragraph);
      }
    },
    { tag: "init-mentions" }
  );
}
